import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CurrencyItem, PageTitle } from "../../../../components/ui-kit";
import { useTableContext } from "../../../../components/ui-kit/ReactTable/context/TableContext";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import { ordersSearchConfig } from "../config/SearchConfig";
import { OrdersTable } from "../tables/OrdersTable";
import { AdvancedSearch } from "../../../../components/advanced-search/AdvancedSearch";
import { OrdersFilters, useGetOrders } from "../hooks/useGetOrders";
import { CURRENCIES, CurrencyCodes } from "@shared_packages/toolkit/types";
import { firstCurrencyItemAdvancedSearch } from "../../../../constants/currencies";
import { ISortConfig } from "../../../../components/ui-kit/ReactTable/context/TableContext";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const TableContainer = styled.div<{ details?: boolean }>`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  ${({ details }) =>
    !details &&
    `border: 1px solid #eeeeee;
    border-radius: 10px;
    padding: 30px 20px;`}
  box-sizing: border-box;

  @media (max-width: 900px) {
    padding: ${({ details }) => (details ? "0" : "12px")};
  }
`;

export interface OrdersListProps {
  ownerId?: string;
  className?: string;
  details?: boolean;
}

export const OrdersList: React.FunctionComponent<OrdersListProps> = ({
  className,
  ownerId,
  details,
}) => {
  const { sortConfig } = useTableContext();
  const [currentCurrency, setCurrentCurrency] = useState<CurrencyItem>(
    firstCurrencyItemAdvancedSearch
  );
  const [filters, setFilters] = useState<OrdersFilters>({});
  const [sort, setSort] = useState<ISortConfig>(() => {
    // Initialize with sortConfig if available, otherwise use default
    return (
      sortConfig || {
        field: "createdAt",
        direction: "desc",
      }
    );
  });

  const {
    orders,
    onPaginationChange,
    totalDocs,
    limit,
    page,
    refreshOrders,
    loading,
  } = useGetOrders(filters, sort, ownerId);

  const [selectedOrderIndexes, setSelectedOrderIndexes] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    // Only update sort if sortConfig is defined and different from current sort
    if (
      sortConfig &&
      sortConfig.field &&
      (sortConfig.field !== sort.field ||
        sortConfig.direction !== sort.direction)
    ) {
      setSort(sortConfig);
    }
  }, [sortConfig, sort.field, sort.direction]);

  // useEffect(() => {
  //   refreshOrders();
  // }, []);

  return (
    <AnimateAppearanceWrapper>
      <Container className={className}>
        <TopLine>
          <PageTitle title="Orders" />
        </TopLine>
        <TableContainer details={details}>
          <AdvancedSearch
            config={ordersSearchConfig}
            onChange={(f: OrdersFilters) => {
              setFilters(f);
              const localCurrency = f?.currency
                ? CURRENCIES[f?.currency as CurrencyCodes]
                : undefined;
              setCurrentCurrency(
                localCurrency
                  ? {
                      label: localCurrency?.name,
                      countryCode: localCurrency?.countryCode,
                      currencyCode: (f?.currency ?? "") as CurrencyCodes,
                      symbol: localCurrency?.symbol,
                    }
                  : firstCurrencyItemAdvancedSearch
              );
            }}
            externalCurrentCurrency={currentCurrency}
          />
          <OrdersTable
            refreshOrders={refreshOrders}
            type="full"
            data={orders}
            pagination={{ page, totalDocs, limit, onPaginationChange }}
            rowSelection={{
              setRowSelection: setSelectedOrderIndexes,
              rowSelection: selectedOrderIndexes,
            }}
          />
        </TableContainer>
      </Container>
    </AnimateAppearanceWrapper>
  );
};
